import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { adminAPI } from '../../services/api';
import { toast } from 'react-toastify';
import { FiPlus, FiEdit2, FiTrash2, FiImage, FiToggleLeft, FiToggleRight } from 'react-icons/fi';
import { formatDate } from '../../utils/helpers';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import './Manage.css';

const ManageBlogs = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    title: '', content: '', excerpt: '', category: '', tags: '',
    seoTitle: '', seoDescription: '', seoKeywords: '', isPublished: false,
  });
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const loadData = async () => {
    try {
      const res = await adminAPI.getBlogs({ page, pageSize: 10 });
      if (res.success) { setBlogs(res.data.items); setTotalPages(res.data.totalPages); }
    } catch (err) { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadData(); }, [page]);

  const openCreate = () => {
    setEditing(null);
    setForm({ title: '', content: '', excerpt: '', category: '', tags: '', seoTitle: '', seoDescription: '', seoKeywords: '', isPublished: false });
    setShowModal(true);
  };

  const openEdit = (blog) => {
    setEditing(blog);
    setForm({
      title: blog.title, content: blog.content, excerpt: blog.excerpt || '',
      category: blog.category, tags: blog.tags,
      seoTitle: blog.seoTitle || '', seoDescription: blog.seoDescription || '',
      seoKeywords: blog.seoKeywords || '', isPublished: blog.isPublished,
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) { await adminAPI.updateBlog(editing.id, form); toast.success('Updated'); }
      else { await adminAPI.createBlog(form); toast.success('Created'); }
      setShowModal(false); loadData();
    } catch (err) { toast.error('Operation failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this blog?')) return;
    try { await adminAPI.deleteBlog(id); toast.success('Deleted'); loadData(); }
    catch (err) { toast.error('Failed to delete'); }
  };

  const handleTogglePublish = async (id) => {
    try { await adminAPI.togglePublish(id); toast.success('Toggled'); loadData(); }
    catch (err) { toast.error('Failed'); }
  };

  const handleImageUpload = async (blogId, file) => {
    try { await adminAPI.uploadBlogImage(blogId, file); toast.success('Image uploaded'); loadData(); }
    catch (err) { toast.error('Upload failed'); }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="manage-page">
      <Helmet><title>Manage Blogs | Portfolio CMS</title></Helmet>
      <div className="manage-header">
        <h2>Blog Management</h2>
        <button className="btn btn-primary" onClick={openCreate}><FiPlus /> New Blog</button>
      </div>
      <div className="manage-table">
        <table>
          <thead><tr><th>Title</th><th>Category</th><th>Status</th><th>Views</th><th>Date</th><th>Image</th><th>Actions</th></tr></thead>
          <tbody>
            {blogs.map(b => (
              <tr key={b.id}>
                <td><strong>{b.title}</strong></td>
                <td><span className="status-pill">{b.category}</span></td>
                <td><span className={`status-pill ${b.isPublished ? 'completed' : ''}`}>{b.isPublished ? 'Published' : 'Draft'}</span></td>
                <td>{b.viewCount}</td>
                <td>{formatDate(b.createdAt)}</td>
                <td>
                  <label className="upload-btn-sm">
                    <FiImage />
                    <input type="file" hidden accept="image/*" onChange={e => e.target.files[0] && handleImageUpload(b.id, e.target.files[0])} />
                  </label>
                </td>
                <td>
                  <div className="action-btns">
                    <button className="btn-icon edit" onClick={() => openEdit(b)}><FiEdit2 /></button>
                    <button className="btn-icon" style={{background: 'rgba(0,184,148,0.1)', color: '#00b894'}} onClick={() => handleTogglePublish(b.id)}>
                      {b.isPublished ? <FiToggleRight /> : <FiToggleLeft />}
                    </button>
                    <button className="btn-icon delete" onClick={() => handleDelete(b.id)}><FiTrash2 /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="pagination" style={{marginTop: 20}}>
          {[...Array(totalPages)].map((_, i) => (
            <button key={i} className={`page-btn ${page === i+1 ? 'active' : ''}`} onClick={() => setPage(i+1)}>{i+1}</button>
          ))}
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{maxWidth: '700px'}}>
            <h2>{editing ? 'Edit Blog' : 'New Blog'}</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-group"><label>Title</label><input value={form.title} onChange={e => setForm({...form, title: e.target.value})} required /></div>
              <div className="form-row">
                <div className="form-group"><label>Category</label><input value={form.category} onChange={e => setForm({...form, category: e.target.value})} /></div>
                <div className="form-group"><label>Tags (comma separated)</label><input value={form.tags} onChange={e => setForm({...form, tags: e.target.value})} /></div>
              </div>
              <div className="form-group"><label>Excerpt</label><textarea rows="2" value={form.excerpt} onChange={e => setForm({...form, excerpt: e.target.value})} /></div>
              <div className="form-group"><label>Content (HTML)</label><textarea rows="8" value={form.content} onChange={e => setForm({...form, content: e.target.value})} required /></div>

              <h3 style={{fontSize:'1rem', marginTop:20, marginBottom:12, color: 'var(--text-secondary)'}}>SEO Metadata</h3>
              <div className="form-group"><label>SEO Title</label><input value={form.seoTitle} onChange={e => setForm({...form, seoTitle: e.target.value})} /></div>
              <div className="form-group"><label>SEO Description</label><textarea rows="2" value={form.seoDescription} onChange={e => setForm({...form, seoDescription: e.target.value})} /></div>
              <div className="form-group"><label>SEO Keywords</label><input value={form.seoKeywords} onChange={e => setForm({...form, seoKeywords: e.target.value})} /></div>

              <div className="form-group"><label><input type="checkbox" checked={form.isPublished} onChange={e => setForm({...form, isPublished: e.target.checked})} /> Publish immediately</label></div>

              <div className="modal-actions">
                <button type="button" className="btn btn-outline" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary">{editing ? 'Update' : 'Create'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageBlogs;
